import React, { useRef, useEffect, useState } from "react";
import { Form } from "react-bootstrap";

declare global {
    interface Window {
        google: any;
    }
}

interface IProps {
    value?: string;
    onChange: (value: string) => void;
    onPlaceSelect?: (place: any) => void;
    placeholder?: string;
    isInvalid?: boolean;
}

const GooglePlacesAutocomplete: React.FC<IProps> = ({
    value = "",
    onChange,
    onPlaceSelect,
    placeholder,
    isInvalid,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const autocompleteRef = useRef<any>(null);
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    useEffect(() => {
        if (!apiKey) {
            console.error("Google Maps API key is not configured");
            return;
        }

        // Check if Google Maps is already loaded
        if (window.google && window.google.maps && window.google.maps.places) {
            setIsScriptLoaded(true);
            return;
        }

        // Check if script is already being loaded
        const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
        if (existingScript) {
            const checkLoaded = () => {
                if (window.google && window.google.maps && window.google.maps.places) {
                    setIsScriptLoaded(true);
                } else {
                    setTimeout(checkLoaded, 100);
                }
            };
            checkLoaded();
            return;
        }

        // Load Google Maps script
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => setIsScriptLoaded(true);
        document.head.appendChild(script);
    }, [apiKey]);

    useEffect(() => {
        if (!isScriptLoaded || !inputRef.current || !window.google) return;

        // Clean up any existing autocomplete instance
        if (autocompleteRef.current) {
            window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
            autocompleteRef.current = null;
        }

        // Initialize autocomplete with Switzerland region restriction
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
            inputRef.current,
            {
                types: ["address"],
                fields: ["formatted_address", "geometry", "address_components", "place_id"],
                componentRestrictions: { country: "ch" },
            }
        );

        // Handle place selection
        const handlePlaceChanged = () => {
            const place = autocompleteRef.current.getPlace();

            if (place && place.formatted_address) {
                onChange(place.formatted_address);

                const locationData = {
                    address: place.formatted_address,
                    placeId: place.place_id,
                    coordinates: place.geometry?.location
                        ? {
                            lat: place.geometry.location.lat(),
                            lng: place.geometry.location.lng(),
                        }
                        : null,
                    addressComponents: place.address_components,
                };

                onPlaceSelect?.(locationData);
            }
        };

        // Add listener
        const listener = autocompleteRef.current.addListener("place_changed", handlePlaceChanged);

        // Cleanup
        return () => {
            if (listener) {
                window.google.maps.event.removeListener(listener);
            }
            if (autocompleteRef.current) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
            }
        };
    }, [isScriptLoaded]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (autocompleteRef.current && window.google) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
                autocompleteRef.current = null;
            }
        };
    }, []);

    return (
        <Form.Control
            ref={inputRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            isInvalid={isInvalid}
            autoComplete="off"
        />
    );
};

export default GooglePlacesAutocomplete;